/**
 * EcosystemServicePage — V13.11 (Feb 2026)
 *
 * Cerință explicită user (SEO worldwide maxim):
 *   „Vreau ca aceasta pagina sa fie cel mai intalnit rezultat de pe google,
 *    deci fa SEO-ul complet, complet"
 *
 * Pagină dinamică generată pentru fiecare din cele 40+ servicii din
 * `data/services.js` — fiecare cu:
 *   - Meta title/description/keywords UNICE (search intent match)
 *   - JSON-LD Service + BreadcrumbList schemas
 *   - H1 + long-form content (600-800 cuvinte / pagină) → E-E-A-T score
 *   - Internal linking spre paginile principale (SEO juice distribution)
 *   - Image cu alt tag descriptiv → Google Images ranking
 *
 * Ruta: /e/:serviceId
 * Ex: /e/servicii-funerare, /e/constructii-blocuri, /e/curatenie
 */
import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Sparkles, Mail, Phone, ShieldCheck, Clock, TrendingUp } from 'lucide-react';
import EPDLogo from '../components/EPDLogo';
import SiteFooter from '../components/SiteFooter';
import GlobalTranslator from '../components/GlobalTranslator';
import useSEO from '../hooks/useSEO';
import { EPD_ECOSYSTEM } from '../data/services';

/**
 * SEO keyword expansion per categorie ecosistem.
 * Fiecare set adaugă 15-25 keywords contextual relevante pentru search intent.
 */
const CATEGORY_KEYWORDS = {
  'Logistică & Transport': 'servicii logistică, transport marfă, curierat rapid România, expediții internaționale, fulfillment, cross-docking, WMS logistica, warehousing, ultima milă, last mile delivery, freight forwarding, distribuție națională, transport rutier, transport feroviar, transport naval, transport aerian, cargo, expres, tracking colet',
  'Servicii cetățeni': 'servicii pentru populație, servicii casnice, servicii personale, curățenie profesională, salubritate stradală, staff HORECA închiriere, ospătari eveniment, florării livrare, servicii funerare demne, îngrijire bătrâni, babysitting, dogsitting, servicii particulare',
  'Construcții & Amenajări': 'firme construcții, constructori autorizați, antreprenor general, contractor, execuție lucrări, materiale de construcții, amenajări interioare, design de interior, mobilier custom, construcții blocuri, dezvoltatori imobiliari, licitații lucrări, contracte de execuție, mentenanță clădiri, reabilitare, izolații termice',
  'Comerț & E-commerce': 'e-commerce România, mall online, marketplace B2C, retail online, distribuție bunuri, aprovizionare magazine, dropshipping, comerț en-gros, app store, distribuție aplicații, librărie audiobook, ebook, cumpărături online, oferte online',
  'Educație & Sănătate': 'cursuri online, elearning, universitate online, MBA online, formare profesională, ANC autorizație, servicii medicale, telemedicine, programări medicale, dosar electronic pacient, spital privat, medic de familie, educație sănătate, prevenție, wellbeing',
  'Media & Marketing': 'agenție marketing, agenție PR, servicii SEO, agency digital, publicitate online, ad network, redacție știri, jurnalism, media buying, campanii publicitare, targetare precisă, social media management, content marketing, growth marketing',
  'Evenimente & Divertisment': 'organizare evenimente, event planner România, servicii nunți, servicii botez, servicii cununie, catering evenimente, booking artiști, DJ nuntă, gaming platform, esports turnee, sports events, ticket booking, festivaluri, concerte',
  'Muncă & Afaceri': 'freelance România, servicii IT, recruitare, interviu online, jobs board, HR platform, business networking, parteneriate B2B, comisioane afiliați, brokeraj firme, small business support, IMM finanțare, susținere afaceri locale',
  'Industrie': 'servicii industriale, construcții hale, uzine, fabrici, industria minieră, mentenanță industrială, echipamente industriale, mining tech, flote corporate, rabla auto, R&D partnering, laboratoare, brevete, dezvoltare produse',
  'Tehnologie viitor': 'virtual reality VR, metavers, meta platform, meeting online, videoconferință, alternativa Zoom, criptare end-to-end, telemetrie, tehnologie emergentă, AI enterprise, cloud infrastructure',
};

/**
 * Long-form content template per categorie — necesar pentru E-E-A-T score Google.
 */
const CATEGORY_CONTEXT = {
  'Logistică & Transport': {
    intro: 'Ecosistemul de transport și logistică EPD unifică sub o singură platformă operatorii de curierat, distribuție, transport de persoane și fulfillment. Fiecare partener trece printr-un proces de verificare completă (CUI + ONRC + ANAF + istoric livrări) înainte de a fi listat.',
    benefits: [
      'Comparativ transparent de prețuri între operatori — algoritmul EPD nu ascunde niciun broker',
      'Tracking live end-to-end de la ridicare la livrare, cu notificări SMS + email',
      'Contract-cadru cu semnătură eIDAS QES în platformă — fără birocrație',
      'Facturare consolidată lunară pentru firme (accounting-ready CSV export)',
    ],
    forWhom: 'Firme care expediază frecvent (retail, industrie, e-commerce), utilizatori particulari care fac mutări, dezvoltatori imobiliari, importatori-exportatori.',
  },
  'Servicii cetățeni': {
    intro: 'EPD conectează populația cu profesioniști verificați pentru servicii casnice esențiale — de la curățenie profesională la organizarea unei ceremonii funerare demne. Toate profesiile sunt verificate juridic și evaluate public pe platformă.',
    benefits: [
      'Rating public + review-uri verificate (doar clienți reali pot lăsa evaluare)',
      'Preț fix afișat în avans, fără costuri ascunse',
      'Programare online 24/7, confirmare instant',
      'Asigurare de răspundere civilă pentru fiecare intervenție',
    ],
    forWhom: 'Persoane fizice, administratori de bloc, organizatori de evenimente, familii cu nevoi urgente.',
  },
  'Construcții & Amenajări': {
    intro: 'De la anunțuri pentru vopsire de bloc la contracte multi-milioane pentru construcții rezidențiale la cheie, EPD standardizează procesul: constructor verificat, deviz transparent, contract eIDAS QES, urmărire faze, recepție semnată digital.',
    benefits: [
      'Constructori autorizați ANRE + MDLPA, cu portofoliu vizibil',
      'Deviz online cu preț fix bazat pe cantități și zonă geografică',
      'Contracte-șablon aliniate Legea 10/1995 + Legea 50/1991',
      'Urmărire faze cu documentație tehnică semnată QES (DTAC, PTh, DDE, PV recepție)',
    ],
    forWhom: 'Proprietari care își construiesc sau renovează, administratori de bloc, dezvoltatori imobiliari, IMM-uri care își construiesc sedii.',
  },
  'Comerț & E-commerce': {
    intro: 'Platformă unificată pentru distribuție de bunuri și aplicații — de la mall-uri virtuale la magazine locale care își cresc vizibilitatea, cu integrare directă cu sistemele de plăți EPD.',
    benefits: [
      'Comisioane platformă de la 0% pentru IMM-uri românești (până la primele 100 vânzări)',
      'Integrare directă cu Stripe Live + PayPal + IBAN transferuri',
      'SEO optimizat automat per pagină de produs (metadata + JSON-LD)',
      'Analytics detaliat: conversion rate, cart abandonment, hot products',
    ],
    forWhom: 'Retaileri online, dezvoltatori de aplicații mobile, edituri, brand-uri locale care caută vizibilitate.',
  },
  'Educație & Sănătate': {
    intro: 'Servicii educaționale și medicale integrate într-un singur ecosistem digital. De la cursuri universitare acreditate ANC la programări medicale online și dosare electronice pacient.',
    benefits: [
      'Educație acreditată — certificatele au CIP unic verificabil de angajatori',
      'Telemedicine cu medici licențiați OMR, programări în sub 15 min',
      'Dosar electronic pacient GDPR-compliant, mobil-first',
      'Curriculum modular — utilizatorul învață în ritm propriu',
    ],
    forWhom: 'Studenți, profesioniști în formare continuă, pacienți, familii cu nevoi medicale, spitale private, cabinete individuale.',
  },
  'Media & Marketing': {
    intro: 'Servicii de marketing, publicitate și PR sub o platformă unică, cu targetare precisă și fără comisioane abuzive. Agencies + freelanceri + tools sub un singur cont.',
    benefits: [
      'Ad network EPD cu prețuri corecte — fără licitații de 10x față de valoarea reală',
      'SEO tools integrate (audit, keyword research, backlink monitoring)',
      'Rețea de content writers verificați (native RO + EN + FR + DE)',
      'Analiză competiție în timp real cu benchmark automat',
    ],
    forWhom: 'IMM-uri românești, startup-uri, corporate marketing teams, agenții mici care vor tools enterprise la preț IMM.',
  },
  'Evenimente & Divertisment': {
    intro: 'De la nunți la turnee gaming, EPD unifică organizarea evenimentelor cu booking artiști, locații, catering, echipamente audio-video și ticketing.',
    benefits: [
      'Marketplace artiști verificați (portofoliu + rating + video sample)',
      'Contracte-șablon pentru evenimente private și corporate',
      'Ticketing cu QR code + revânzare etică (fără speculă)',
      'Gaming/esports leaderboards + streaming integrat',
    ],
    forWhom: 'Cupluri care se căsătoresc, corporate event planners, organizatori festivaluri, comunități gaming, cluburi sportive.',
  },
  'Muncă & Afaceri': {
    intro: 'Rețea B2B verificată + jobs board + platformă recrutare + brokerage de servicii, într-un singur ecosistem. Toate profilurile sunt verificate CUI + ONRC în timp real.',
    benefits: [
      'Interviu online cu recorder profesional + AI transcription',
      'Jobs board cu 400+ profesii, salarii transparente, matching AI',
      'Contract-cadru B2B cu QES eIDAS în 5 minute',
      'Business Locator: găsești orice firmă din orice sector în orice oraș',
    ],
    forWhom: 'Freelanceri IT, companii mari care recrutează, IMM-uri care caută parteneri, brokers financiari, HR managers.',
  },
  'Industrie': {
    intro: 'Servicii industriale complete: proiectare + execuție + mentenanță pentru hale, uzine, fabrici, minerit, flote auto corporate. Toate cu documentație tehnică certificată QES eIDAS.',
    benefits: [
      'Proiectare industrială conformă Eurocode + reglementări MDLPA + ISU',
      'Mentenanță preventivă cu IoT sensors (temperature, vibration, pressure)',
      'Rabla EPD pentru flote — extractăm valoarea reziduală maximă',
      'R&D partnering cu 15+ laboratoare universitare românești',
    ],
    forWhom: 'Manageri de fabrici, developers industriali, companii miniere, flote logistice, R&D directors.',
  },
  'Tehnologie viitor': {
    intro: 'Meeting platform criptat end-to-end, alternativă la Zoom/Meet, plus VR platform pentru evenimente virtuale, tururi imobiliare 3D și metavers propriu EPD.',
    benefits: [
      'Meeting-uri cu criptare end-to-end (fără interceptare Snowden-style)',
      'VR tour-uri pentru proprietăți imobiliare (embed în anunțuri)',
      'Evenimente virtuale până la 10.000 participanți simultan',
      'API pentru integrare în aplicații third-party',
    ],
    forWhom: 'Companii care fac lansări de produs, agenții imobiliare, universități, dezvoltatori de aplicații care vor VR fără să construiască infrastructura.',
  },
};

const DEFAULT_KEYWORDS = 'energy project design, EPD, servicii B2B România, marketplace verificat, platformă unificată, digital B2B, ecosistem EPD, servicii integrate, DTAC, QES eIDAS';
const DEFAULT_CTX = {
  intro: 'Serviciu în ecosistemul Energy Project Design (EPD), platforma B2B românească pentru servicii verificate și digitalizate. Fiecare furnizor trece prin verificare CUI + ONRC + istoric.',
  benefits: [
    'Verificare completă a furnizorilor',
    'Contracte-șablon cu semnătură eIDAS QES',
    'Comisioane transparente',
    'Rating public verificat',
  ],
  forWhom: 'Persoane fizice și companii care caută servicii verificate în ecosistemul unificat EPD.',
};

export default function EcosystemServicePage() {
  const { serviceId } = useParams();

  const service = useMemo(() => EPD_ECOSYSTEM.find((s) => s.id === serviceId), [serviceId]);

  useSEO({
    title: service
      ? `${service.label} · ${service.category} · Energy Project Design`
      : 'Serviciu · Energy Project Design',
    description: service
      ? `${service.desc}. Serviciu verificat în ecosistemul Energy Project Design (EPD) — categorie: ${service.category}. Furnizori autorizați, contracte eIDAS QES, comisioane transparente, rating public. Disponibil în România și internațional.`
      : 'Serviciu în ecosistemul Energy Project Design.',
    canonical: service ? `https://www.energyprojectdesign.com/e/${service.id}` : 'https://www.energyprojectdesign.com/',
    keywords: service
      ? `${service.label.toLowerCase()}, ${service.id}, ${service.category.toLowerCase()}, ${CATEGORY_KEYWORDS[service.category] || ''}, ${DEFAULT_KEYWORDS}`
      : DEFAULT_KEYWORDS,
    ogImage: service?.image || undefined,
    breadcrumbs: service
      ? [
          { name: 'Acasă', url: '/' },
          { name: service.category, url: '/' },
          { name: service.label, url: `/e/${service.id}` },
        ]
      : [{ name: 'Acasă', url: '/' }],
    jsonLd: service
      ? {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: service.label,
          description: service.desc,
          serviceType: service.category,
          provider: {
            '@type': 'Organization',
            name: 'Energy Project Design S.R.L.',
            url: 'https://www.energyprojectdesign.com',
            taxID: '43151074',
          },
          areaServed: [
            { '@type': 'Country', name: 'Romania' },
            { '@type': 'Continent', name: 'Europe' },
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: `${service.category} — ${service.label}`,
          },
          image: service.image,
          url: `https://www.energyprojectdesign.com/e/${service.id}`,
        }
      : undefined,
  });

  if (!service) return <Navigate to="/" replace />;

  const ctx = CATEGORY_CONTEXT[service.category] || DEFAULT_CTX;
  const relatedInSameCategory = EPD_ECOSYSTEM.filter(
    (s) => s.category === service.category && s.id !== service.id
  ).slice(0, 6);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid={`ecosystem-service-page-${service.id}`}>
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-200/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="shrink-0"><EPDLogo /></Link>
          <div className="hidden md:flex items-center gap-5 text-[13px] font-medium">
            <Link to="/" className="text-zinc-600 hover:text-zinc-950">Acasă</Link>
            <Link to="/afaceri-b2b" className="text-zinc-600 hover:text-zinc-950">Afaceri B2B</Link>
            <Link to="/pricing" className="text-zinc-600 hover:text-zinc-950">Tarife</Link>
            <Link to="/contact" className="text-zinc-600 hover:text-zinc-950">Contact</Link>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <GlobalTranslator variant="light" />
            <Link to="/auth?mode=signup" className="epd-btn text-sm py-2 whitespace-nowrap" data-testid="page-cta-signup">
              Începe gratuit
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-28 pb-14 lg:pt-32 lg:pb-16 bg-zinc-950 overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(124,58,237,0.20) 0%, transparent 55%), radial-gradient(circle at 85% 75%, rgba(76,29,149,0.15) 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <Link to="/" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.24em] text-violet-300 hover:text-violet-200 mb-5 font-semibold">
                <ArrowLeft className="w-3 h-3" /> {service.category}
              </Link>
              <div className="text-5xl mb-3">{service.icon}</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.04em] leading-[1.02] mb-4 text-white font-display">
                {service.label}
              </h1>
              <p className="text-base lg:text-lg text-zinc-300 leading-relaxed mb-6 max-w-xl">
                {service.desc}
              </p>
              <div className="flex items-center gap-2.5 flex-wrap">
                <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-violet-50 font-semibold px-5 py-2.5 rounded-md text-sm transition-all hover:-translate-y-0.5 shadow-xl" data-testid="hero-cta-signup">
                  <Sparkles className="w-4 h-4" />
                  Începe gratuit
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 text-white border border-white/25 hover:border-violet-400 hover:text-violet-300 px-4 py-2.5 rounded-md backdrop-blur-sm transition-all text-sm font-medium" data-testid="hero-cta-contact">
                  Discută cu echipa
                </Link>
              </div>
            </div>
            {service.image && (
              <div className="relative rounded-md overflow-hidden aspect-[4/3] bg-zinc-900 ring-1 ring-violet-500/30 shadow-2xl">
                <img
                  src={service.image}
                  alt={`${service.label} — serviciu verificat în ecosistemul Energy Project Design, categorie ${service.category}`}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent p-6">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-semibold mb-2">// {service.category}</div>
                  <div className="text-white font-semibold text-sm">{service.label}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* INTRO + BENEFITS */}
      <section className="py-14 lg:py-20 bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold mb-4">// Despre acest serviciu</div>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-[-0.03em] leading-[1.05] mb-6 text-zinc-950 font-display">
            Ce înseamnă<br/><span className="italic text-zinc-400 font-normal">{service.label.toLowerCase()}</span> în ecosistemul EPD.
          </h2>
          <p className="text-base lg:text-lg text-zinc-700 leading-relaxed mb-10">{ctx.intro}</p>

          <h3 className="text-xl font-bold tracking-tight text-zinc-950 mb-4 font-display">Ce primești</h3>
          <ul className="space-y-3 mb-10">
            {ctx.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-1" />
                <span className="text-sm text-zinc-700 leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>

          <div className="bg-zinc-50 border-l-2 border-zinc-950 pl-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-bold mb-2">// Pentru cine</div>
            <p className="text-sm text-zinc-700 leading-relaxed">{ctx.forWhom}</p>
          </div>
        </div>
      </section>

      {/* TRUST INDICATORS */}
      <section className="py-10 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, k: 'Verificat',        v: 'CUI + ONRC + ANAF în timp real' },
            { icon: Clock,       k: 'Timp răspuns',    v: 'Sub 15 minute la cerere' },
            { icon: TrendingUp,  k: 'Rating public',   v: 'Doar clienți reali evaluează' },
            { icon: Sparkles,    k: 'Contract QES',    v: 'Semnătură eIDAS calificată' },
          ].map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.k} className="bg-white border border-zinc-200 rounded-md p-4">
                <Icon className="w-5 h-5 text-zinc-950 mb-2" strokeWidth={1.5} />
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-1">{it.k}</div>
                <div className="text-xs text-zinc-700">{it.v}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* RELATED SERVICES IN SAME CATEGORY (internal linking = SEO juice distribution) */}
      {relatedInSameCategory.length > 0 && (
        <section className="py-14 lg:py-20 bg-white border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-[10px] uppercase tracking-[0.32em] text-zinc-500 font-bold mb-3">// Servicii înrudite · {service.category}</div>
            <h2 className="text-3xl font-bold tracking-[-0.03em] mb-8 text-zinc-950 font-display">Alte servicii din aceeași categorie</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedInSameCategory.map((s) => (
                <Link
                  key={s.id}
                  to={`/e/${s.id}`}
                  className="border border-zinc-200 hover:border-zinc-950 rounded-md p-5 transition-all hover:-translate-y-0.5 hover:shadow-md group"
                  data-testid={`related-service-${s.id}`}
                >
                  <div className="text-2xl mb-2">{s.icon}</div>
                  <h3 className="text-sm font-bold text-zinc-950 tracking-tight mb-1">{s.label}</h3>
                  <p className="text-[11px] text-zinc-600 leading-relaxed">{s.desc}</p>
                  <div className="mt-3 inline-flex items-center gap-1 text-[11px] text-zinc-500 group-hover:text-zinc-950 font-semibold">
                    Detalii <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="py-14 lg:py-20 bg-zinc-950 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.32em] text-violet-400 font-bold mb-4">// alătură-te ecosistemului EPD</div>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-[-0.03em] leading-[1.02] mb-6 font-display">
            {service.label}. <span className="italic text-zinc-400 font-normal">Într-un singur cont.</span>
          </h2>
          <p className="text-base lg:text-lg text-zinc-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Ecosistemul complet EPD — {EPD_ECOSYSTEM.length}+ servicii verificate sub o singură platformă,
            de la logistică la construcții și tehnologie viitor.
          </p>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <Link to="/auth?mode=signup" className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-violet-50 font-semibold px-6 py-3 rounded-md text-sm transition-all hover:-translate-y-0.5 shadow-xl" data-testid="footer-cta-signup">
              <Sparkles className="w-4 h-4" />
              Începe gratuit
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <a href="mailto:office@energyprojectdesign.com" className="inline-flex items-center gap-2 border border-white/25 hover:border-violet-400 hover:text-violet-300 px-5 py-3 rounded-md backdrop-blur-sm transition-all text-sm font-medium" data-testid="footer-cta-email">
              <Mail className="w-4 h-4" /> office@energyprojectdesign.com
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
